import { Button } from '@/components/base/button'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { ScrollArea } from '@/components/base/scroll-area'
import { Separator } from '@/components/base/separator'
import { Switch } from '@/components/base/switch'
import { Icon } from '@/components/common/icons/Icon'
import { Feature } from '@/components/Feature'
import { SliderSetMicrophone } from '@/components/SliderSetMicrophone'
import { SliderSetNoiseGate } from '@/components/SliderSetNoiseGate'
import { SliderSetSpeakerVolume } from '@/components/SliderSetSpeakerVolume'
import { SliderWithTicks } from '@/components/SliderWithTicks'
import { SwitchAutoGainControl } from '@/components/SwitchAutoGainControl'
import { SwitchEchoCancellation } from '@/components/SwitchEchoCancellation'
import { SwitchMirrorLocalVideo } from '@/components/SwitchMirrorLocalVideo'
import { SwitchNoiseSuppression } from '@/components/SwitchNoiseSuppression'
import { SwitchRemoveBackgroundNoise } from '@/components/SwitchRemoveBackgroundNoise'
import { TEST_IDS } from '@/helpers/constants'
import { cn } from '@/helpers/utils'
import { useMicrophoneSensitivityState } from '@/hooks/useMicrophoneSensitivityState'
import { useMicrophoneVolumeState } from '@/hooks/useMicrophoneVolumeState'
import { useSetAudioFlagsState } from '@/hooks/useSetAudioFlagsState'
import { useSpeakerVolumeState } from '@/hooks/useSpeakerVolumeState'
import { logger } from '@/logger/createLogger'
import { FEATURES } from '@/stores/features'
import { useRoomStore } from '@/stores/room'
import { useUiStore } from '@/stores/ui'
import { useId, useState } from 'react'

interface UseGetAdvancedControlStates {
  memberId: string
}

const useGetAdvancedControlStates = ({
  memberId,
}: UseGetAdvancedControlStates) => {
  const { isMicrophoneVolumeEnabled, isMicrophoneVolumeVisible } =
    useMicrophoneVolumeState({ memberId })

  const { isSpeakerVolumeEnabled, isSpeakerVolumeVisible } =
    useSpeakerVolumeState({ memberId })

  const { isMicrophoneSensitivityEnabled, isMicrophoneSensitivityVisible } =
    useMicrophoneSensitivityState({ memberId })

  const { isSetAudioFlagsEnabled, isSetAudioFlagsVisible } =
    useSetAudioFlagsState({ memberId })

  return {
    isMicrophoneSensitivityEnabled,
    isMicrophoneSensitivityVisible,
    isMicrophoneVolumeEnabled,
    isMicrophoneVolumeVisible,
    isSetAudioFlagsEnabled,
    isSetAudioFlagsVisible,
    isSpeakerVolumeEnabled,
    isSpeakerVolumeVisible,
  }
}

export const PanelRoomAdvanced = () => {
  const isAudioOnly = useRoomStore(state => state.isAudioOnly)
  const memberId = useRoomStore(state => state.memberId)
  const selectedMemberId = useUiStore(state => state.selectedMemberId)

  const roomMemberEntity = useRoomStore(state =>
    state.membersMap.get(selectedMemberId),
  )

  const isSelf = memberId === selectedMemberId

  const [displayBannerEnabled, setDisplayBannerEnabled] = useState(false)

  const {
    isMicrophoneSensitivityEnabled,
    isMicrophoneSensitivityVisible,
    isMicrophoneVolumeEnabled,
    isMicrophoneVolumeVisible,
    isSetAudioFlagsEnabled,
    isSetAudioFlagsVisible,
    isSpeakerVolumeEnabled,
    isSpeakerVolumeVisible,
  } = useGetAdvancedControlStates({ memberId: selectedMemberId })

  const videoQualityId = useId()
  const lowBitrateModeId = useId()
  const displayBannerId = useId()
  const displayBannerInputId = useId()

  if (!roomMemberEntity) {
    logger.warn(
      `Room member entity not found for memberId: ${selectedMemberId}`,
    )
    return (
      <div className="flex h-full flex-col items-center justify-start gap-y-2 py-3">
        <div className="flex items-center gap-x-2">
          <span>
            <Icon tag="warning" size="xl" />
          </span>
          <h2 className="text-lg font-semibold">Participant not found</h2>
        </div>
        <p className="text-xs">The participant may have left the room</p>
        <p className="text-xs">Please select a different participant</p>
      </div>
    )
  }

  const {
    autoGain,
    echoCancellation,
    microphoneVolume,
    name,
    noiseGate,
    noiseSuppression,
    removeBackgroundNoise,
    speakerVolume,
  } = roomMemberEntity

  return (
    <ScrollArea
      aria-label="Advanced Settings Toolbar"
      className="h-full"
      data-testid={TEST_IDS.ROOM_PANEL_ADVANCED}
      role="toolbar"
    >
      <div className="pl-1">
        <div className="w-full gap-y-4 pr-4 text-foreground" role="group">
          <div className="flex flex-col gap-y-2">
            {/* TODO: use line-clamp or truncate and show tooltip like in member overlay pattern? */}
            <h3 className={cn('line-clamp-2 text-sm', isSelf && 'sr-only')}>
              Settings for {name}
            </h3>
            <h4 className="text-sm">Audio Settings</h4>
          </div>
          <ul>
            {isMicrophoneVolumeVisible && (
              <li className="flex flex-col gap-y-1">
                <SliderSetMicrophone
                  disabled={!isMicrophoneVolumeEnabled}
                  memberId={selectedMemberId}
                  microphoneVolume={microphoneVolume}
                />
              </li>
            )}
            {isSpeakerVolumeVisible && (
              <li className="flex flex-col gap-y-1">
                <SliderSetSpeakerVolume
                  disabled={!isSpeakerVolumeEnabled}
                  memberId={selectedMemberId}
                  speakerVolume={speakerVolume}
                />
              </li>
            )}
            {isMicrophoneSensitivityVisible && (
              <li className="mb-2 flex flex-col gap-y-1">
                <SliderSetNoiseGate
                  disabled={!isMicrophoneSensitivityEnabled}
                  memberId={selectedMemberId}
                  noiseGate={noiseGate}
                />
              </li>
            )}
            <Feature
              name={
                isSelf
                  ? FEATURES.SELF_BACKGROUND_NOISE
                  : FEATURES.MEMBER_BACKGROUND_NOISE
              }
            >
              {/* TODO: implement background noise once exposed on SDK */}
              <li className="flex items-center justify-between py-1.5">
                <SwitchRemoveBackgroundNoise
                  // TODO: remove disabled once implemented
                  disabled={true}
                  memberId={selectedMemberId}
                  removeBackgroundNoise={removeBackgroundNoise}
                />
              </li>
            </Feature>
            <Feature
              name={
                isSelf
                  ? FEATURES.SELF_ECHO_CANCELLATION
                  : FEATURES.MEMBER_ECHO_CANCELLATION
              }
            >
              {isSetAudioFlagsVisible && (
                <li className="flex items-center justify-between py-1.5">
                  <SwitchEchoCancellation
                    disabled={!isSetAudioFlagsEnabled}
                    echoCancellation={echoCancellation}
                    memberId={selectedMemberId}
                  />
                </li>
              )}
            </Feature>
            <Feature
              name={
                isSelf
                  ? FEATURES.SELF_NOISE_SUPPRESSION
                  : FEATURES.MEMBER_NOISE_SUPPRESSION
              }
            >
              {isSetAudioFlagsVisible && (
                <li className="flex items-center justify-between py-1.5">
                  <SwitchNoiseSuppression
                    disabled={!isSetAudioFlagsEnabled}
                    noiseSuppression={noiseSuppression}
                    memberId={selectedMemberId}
                  />
                </li>
              )}
            </Feature>
            <Feature
              name={
                isSelf
                  ? FEATURES.SELF_AUTO_GAIN_CONTROL
                  : FEATURES.MEMBER_AUTO_GAIN_CONTROL
              }
            >
              {isSetAudioFlagsVisible && (
                <li className="flex items-center justify-between py-1.5">
                  <SwitchAutoGainControl
                    autoGain={autoGain}
                    disabled={!isSetAudioFlagsEnabled}
                    memberId={selectedMemberId}
                  />
                </li>
              )}
            </Feature>
          </ul>
        </div>
        {!isAudioOnly && (
          <>
            <Separator className="my-5 bg-secondary" />
            <div
              className="w-full gap-y-4 pr-4 text-foreground last:mb-1"
              role="group"
            >
              <h4 className="text-sm">Video Settings</h4>
              <ul>
                <Feature
                  name={
                    isSelf
                      ? FEATURES.SELF_VIDEO_QUALITY
                      : FEATURES.MEMBER_VIDEO_QUALITY
                  }
                >
                  {/* TODO: implement video quality */}
                  <li className="flex flex-col gap-y-1">
                    <div className="flex items-center gap-x-3 py-4 pr-2">
                      <Icon size="xl" tag="videocam" />
                      <Label htmlFor={videoQualityId}>Video Quality</Label>
                    </div>
                    <SliderWithTicks
                      disabled
                      id={videoQualityId}
                      stops={2}
                      max={1}
                    />
                  </li>
                </Feature>
                {/* only show Mirror Local Video control if self */}
                {isSelf && (
                  <Feature name={FEATURES.MIRROR_LOCAL_VIDEO}>
                    <li className="flex items-center justify-between py-1.5">
                      <SwitchMirrorLocalVideo />
                    </li>
                  </Feature>
                )}
                <Feature
                  name={
                    isSelf
                      ? FEATURES.SELF_LOW_BITRATE_MODE
                      : FEATURES.MEMBER_LOW_BITRATE_MODE
                  }
                >
                  {/* TODO:  implement low bitrate mode once exposed on SDK */}
                  <li className="flex items-center justify-between py-1.5">
                    <Label htmlFor={lowBitrateModeId}>Low Bitrate Mode</Label>
                    <Switch disabled id={lowBitrateModeId} />
                  </li>
                </Feature>
                <Feature
                  name={
                    isSelf
                      ? FEATURES.USER_DISPLAY_BANNER
                      : FEATURES.MEMBER_USER_DISPLAY_BANNER
                  }
                >
                  {/* TODO: implement display banner once available */}
                  <li>
                    <div className="flex items-center justify-between py-1.5">
                      <Label htmlFor={displayBannerId}>Display Banner</Label>
                      <Switch
                        checked={displayBannerEnabled}
                        disabled
                        id={displayBannerId}
                        onCheckedChange={setDisplayBannerEnabled}
                      />
                    </div>
                    {/* ARIA live region to announce state changes */}
                    <div className="sr-only" aria-live="polite">
                      {displayBannerEnabled
                        ? 'Display banner text is now enabled'
                        : 'Display banner text is now disabled'}
                    </div>
                    <div className="relative flex h-10 w-full flex-col justify-center">
                      <Label className="sr-only" htmlFor={displayBannerInputId}>
                        {displayBannerEnabled
                          ? 'Display Banner text'
                          : 'Display Banner text is disabled'}
                      </Label>
                      {/* TODO: ensure correct aria/wcag for switch enable/disable input field */}
                      <Input
                        id={displayBannerInputId}
                        className="w-full rounded-[10px] pl-4"
                        disabled={!displayBannerEnabled}
                        placeholder="e.g. Be Right Back"
                      />
                      <Button
                        className="absolute right-0"
                        disabled={!displayBannerEnabled}
                        variant="icon"
                      >
                        <Icon tag="publish-with-changes" size="xl" />
                      </Button>
                    </div>
                  </li>
                </Feature>
              </ul>
            </div>
          </>
        )}
      </div>
    </ScrollArea>
  )
}
